import React from 'react';

import styled from 'styled-components';

import { TodayShelf } from './TodayShelf';
import { TomorrowShelf } from './TomorrowShelf';

import { StickyHorizontalScroll } from 'components/StickyHorizontalScroll';

import {
    MicrostoreLocationFormValues,
    MicrostoreProductStockInfoFlat,
    MicrostoreShelfFormValues,
} from 'pages/MicrostoreColumnGroups/MicrostoreColumnGroupDetails/FormHelper/DataMapper';
import { BirthdayDate } from 'helpers/dateTimes';

export const ColumnLocationSelector = ({
    allSiteLocations,
    canBeUpdated = true,
    earliestDLCDateToKeepForStockCalculation,
    isSecured,
    isToday,
    microstoreProductsStockInfo,
    getMicrostoreProductsStockInfoLoading,
    nextSiteDeliveryDate,
    removalDate,
    selectedLocationPosition,
    selectedShelfIndex,
    setIsToday,
    setSelectedLocationPosition,
    setSelectedShelfIndex,
    shelves,
    updateLocationAt,
    updateSelectedLocation,
}: {
    allSiteLocations: MicrostoreLocationFormValues[];
    canBeUpdated?: boolean;
    earliestDLCDateToKeepForStockCalculation: BirthdayDate | null;
    isSecured: boolean;
    isToday: boolean;
    microstoreProductsStockInfo: MicrostoreProductStockInfoFlat[] | null;
    getMicrostoreProductsStockInfoLoading?: boolean;
    nextSiteDeliveryDate: BirthdayDate | null;
    removalDate: string | null;
    selectedLocationPosition: { column: number; row: number } | null;
    selectedShelfIndex: number | null;
    setIsToday: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedLocationPosition: React.Dispatch<React.SetStateAction<{ column: number; row: number } | null>>;
    setSelectedShelfIndex: React.Dispatch<React.SetStateAction<number | null>>;
    shelves: MicrostoreShelfFormValues[];
    updateLocationAt?: (args: {
        shelfIndex: number;
        row: number;
        column: number;
        values: Partial<MicrostoreLocationFormValues>;
    }) => void;
    updateSelectedLocation: (values: Partial<MicrostoreLocationFormValues>) => void;
}) => {
    // Every shelf stretches to the same width — that of the widest shelf — so the grid
    // reads like a real fridge. A double-coil slot counts as two so the widest shelf's
    // single slots land around SINGLE_SLOT_WIDTH before horizontal scrolling kicks in.
    const SINGLE_SLOT_WIDTH = 90;
    const maxRowSlotCount = Math.max(
        1,
        ...shelves.flatMap((shelf) =>
            shelf.locationTable.map((locationRow) =>
                locationRow.reduce(
                    (count, location) =>
                        count + (location.product?.isDoubleCoil || location.replacementProduct?.isDoubleCoil ? 2 : 1),
                    0,
                ),
            ),
        ),
    );
    const rowMinWidthPx = maxRowSlotCount * SINGLE_SLOT_WIDTH;

    return (
        <ScrollFrame>
            <Column>
                {shelves.map((shelf, shelfIndex) => {
                    return (
                        <ShelfContainer key={shelfIndex}>
                            <TodayShelf
                                allSiteLocations={allSiteLocations}
                                canBeUpdated={canBeUpdated}
                                isToday={isToday}
                                microstoreProductsStockInfo={microstoreProductsStockInfo}
                                getMicrostoreProductsStockInfoLoading={getMicrostoreProductsStockInfoLoading}
                                selectedLocationPosition={selectedLocationPosition}
                                selectedShelfIndex={selectedShelfIndex}
                                setIsToday={setIsToday}
                                setSelectedLocationPosition={setSelectedLocationPosition}
                                setSelectedShelfIndex={setSelectedShelfIndex}
                                shelf={shelf}
                                shelfIndex={shelfIndex}
                                rowMinWidthPx={rowMinWidthPx}
                                updateLocationAt={updateLocationAt}
                            />
                            <TomorrowShelf
                                allSiteLocations={allSiteLocations}
                                canBeUpdated={canBeUpdated}
                                earliestDLCDateToKeepForStockCalculation={earliestDLCDateToKeepForStockCalculation}
                                isSecured={isSecured}
                                isToday={isToday}
                                microstoreProductsStockInfo={microstoreProductsStockInfo}
                                getMicrostoreProductsStockInfoLoading={getMicrostoreProductsStockInfoLoading}
                                nextSiteDeliveryDate={nextSiteDeliveryDate}
                                removalDate={removalDate}
                                selectedLocationPosition={selectedLocationPosition}
                                selectedShelfIndex={selectedShelfIndex}
                                setIsToday={setIsToday}
                                setSelectedLocationPosition={setSelectedLocationPosition}
                                setSelectedShelfIndex={setSelectedShelfIndex}
                                shelf={shelf}
                                shelfIndex={shelfIndex}
                                rowMinWidthPx={rowMinWidthPx}
                                updateLocationAt={updateLocationAt}
                                updateSelectedLocation={updateSelectedLocation}
                            />
                        </ShelfContainer>
                    );
                })}
            </Column>
        </ScrollFrame>
    );
};

const ScrollFrame = styled(StickyHorizontalScroll)`
    margin-top: 5px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;

    & > :not(:first-child) {
        border-top: 2px solid ${({ theme }) => theme.darkBorderColor};
        margin-top: 5px;
        padding-top: 5px;
    }
`;

const ShelfContainer = styled.div`
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    break-inside: avoid;
`;
